/**
 * Kiosk Preview Demo Page
 * Demonstrates the unified KioskPreview component
 * Uses the SAME component as the customizer (no duplication)
 */

import React, { useState } from 'react';
import { KioskPreview } from '../components/preview/KioskPreview';
import { KioskUICustomization, ConnectorInfo } from '../types/kiosk-types';
import { getDefaultKioskConfig } from '../constants/kiosk-defaults';

// Sample connector data for demo
const DEMO_CONNECTORS: ConnectorInfo[] = [
  { id: '01', type: 'ccs', power: 50, status: 'available' },
  { id: '02', type: 'chademo', power: 50, status: 'connected' },
  { id: '03', type: 'type2', power: 22, status: 'occupied' },
  { id: '04', type: 'ccs', power: 150, status: 'unavailable' },
  { id: '05', type: 'type2', power: 22, status: 'available' },
  { id: '06', type: 'ccs', power: 350, status: 'available' },
];

export function KioskPreviewDemoPage() {
  const [formFactor, setFormFactor] = useState<'4inch' | '5inch'>('5inch');
  const [showIdleScreen, setShowIdleScreen] = useState(false);
  const [customLogoUrl, setCustomLogoUrl] = useState('');
  const [idleScreenImageUrl, setIdleScreenImageUrl] = useState('');

  // Get default config for selected form factor
  const config: KioskUICustomization = {
    ...getDefaultKioskConfig(formFactor),
    formFactor,
  };

  // Prepare idle screen media if URL provided
  const idleScreenMedia = idleScreenImageUrl
    ? {
        file: new File([], 'dummy'),
        mediaType: idleScreenImageUrl.endsWith('.json') ? ('lottie' as const) : ('image' as const),
        originalImageUrl: idleScreenImageUrl,
        previewUrl: idleScreenImageUrl,
      }
    : undefined;

  // Show 4" connectors for 4" screen, more for 5" screen
  const displayConnectors = formFactor === '4inch' ? DEMO_CONNECTORS.slice(0, 3) : DEMO_CONNECTORS;

  return (
    <div className="flex h-screen overflow-hidden bg-gray-100">
      {/* Left Panel - Controls */}
      <div className="w-96 overflow-y-auto border-r border-gray-300 bg-white p-6">
        <h1 className="mb-4 text-2xl font-bold">Kiosk Preview Demo</h1>
        <div className="mb-6 rounded-lg bg-green-50 border border-green-200 p-3">
          <p className="text-sm font-medium text-green-900">
            ✓ Single Implementation
          </p>
          <p className="text-xs text-green-700 mt-1">
            Uses <code className="bg-green-100 px-1 py-0.5 rounded">KioskPreview</code> - the same component as the customizer
          </p>
        </div>

        {/* Screen Size Selector */}
        <div className="mb-6">
          <h2 className="mb-2 text-lg font-semibold">Screen Size</h2>
          <div className="flex gap-2">
            <button
              className={`rounded px-4 py-2 text-sm font-medium transition-colors ${
                formFactor === '4inch'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
              onClick={() => setFormFactor('4inch')}
            >
              4" (480x480)
            </button>
            <button
              className={`rounded px-4 py-2 text-sm font-medium transition-colors ${
                formFactor === '5inch'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
              onClick={() => setFormFactor('5inch')}
            >
              5" (720x1280)
            </button>
          </div>
        </div>

        {/* Custom Logo */}
        <div className="mb-6">
          <h2 className="mb-2 text-lg font-semibold">Custom Logo</h2>
          <input
            type="text"
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            placeholder="Logo URL (optional)"
            value={customLogoUrl}
            onChange={(e) => setCustomLogoUrl(e.target.value)}
          />
          <p className="mt-1 text-xs text-gray-500">
            Leave empty for default Nayax Energy logo
          </p>
        </div>

        {/* Idle Screen Settings */}
        <div className="mb-6 rounded-lg border border-gray-200 p-4">
          <h2 className="mb-3 text-lg font-semibold">Idle Screen</h2>

          <label className="mb-4 flex items-center gap-2">
            <input
              type="checkbox"
              checked={showIdleScreen}
              onChange={(e) => setShowIdleScreen(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span className="text-sm font-medium">Show Idle Screen</span>
          </label>

          {showIdleScreen && (
            <div className="border-t border-gray-200 pt-3">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Custom Media URL
              </label>
              <input
                type="text"
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                placeholder="Image or Lottie JSON URL"
                value={idleScreenImageUrl}
                onChange={(e) => setIdleScreenImageUrl(e.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500">
                Leave empty for default animation. Use .json for Lottie, image URL for static.
              </p>
            </div>
          )}
        </div>

        {/* Component Architecture */}
        <div className="rounded-lg bg-blue-50 p-4">
          <h3 className="mb-2 font-semibold text-blue-900">Architecture</h3>
          <div className="space-y-2 text-xs text-blue-800">
            <div className="rounded bg-blue-100 p-2">
              <div className="font-medium mb-1">Demo Page:</div>
              <code className="text-[10px]">KioskPreview (direct)</code>
            </div>
            <div className="rounded bg-blue-100 p-2">
              <div className="font-medium mb-1">Customizer:</div>
              <code className="text-[10px]">
                KioskMockup4inch/5inch<br/>
                → KioskPreview (same)
              </code>
            </div>
          </div>
        </div>

        {/* Usage Example */}
        <div className="mt-4 rounded-lg bg-purple-50 p-4">
          <h3 className="mb-2 font-semibold text-purple-900">Usage Anywhere</h3>
          <pre className="text-[10px] text-purple-800 overflow-x-auto whitespace-pre-wrap">
{`<KioskPreview
  config={config}
  connectors={myConnectors}
  showIdleScreen={true}
  onConnectorClick={(c) => {
    console.log(c.id);
  }}
/>`}
          </pre>
        </div>
      </div>

      {/* Right Panel - Preview */}
      <div className="flex flex-1 items-center justify-center overflow-auto bg-gray-100 p-8">
        <KioskPreview
          config={config}
          connectors={displayConnectors}
          pendingLogoUrl={customLogoUrl || undefined}
          pendingIdleScreen={idleScreenMedia}
          showIdleScreen={showIdleScreen}
          onConnectorClick={(connector) => {
            console.log('Connector clicked:', connector.id);
            alert(`Selected Connector ${connector.id} (${connector.type})`);
          }}
        />
      </div>
    </div>
  );
}
